import { useEffect, useState } from 'react';

const MARKETING_COOKIE_ID = 'C0003';
const useNoCookiesBingMapsDomain = window.UseNoCookiesBingMapsDomain === true;

export function useLoadBingMapScript(src: string) {
  type statusValues =
    | 'idle'
    | 'loading'
    | 'ready'
    | 'error'
    | 'noConsentCookie';
  const [status, setStatus] = useState<statusValues>(src ? 'loading' : 'idle');
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const callbackName = 'initMap';
  src = `${src}&callback=${callbackName}`;

  const hasMarketingConsent = (): boolean => {
    if (typeof window.OptanonActiveGroups !== 'undefined') {
      return window.OptanonActiveGroups.includes(MARKETING_COOKIE_ID);
    }
    return false;
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const DUMMY_OPTANON_ACTIVE_GROUPS =
        process.env.REACT_APP_DUMMY_OPTANON_ACTIVE_GROUPS;

      if (DUMMY_OPTANON_ACTIVE_GROUPS) {
        window.OptanonActiveGroups = DUMMY_OPTANON_ACTIVE_GROUPS;
      }
    }
  }, []);

  useEffect(() => {
    // Allow falsy src value if waiting on other data needed for
    // constructing the script URL passed to this hook.
    if (!src) {
      setStatus('idle');
      return;
    }

    // Check for initial cookie consent status
    if (!hasMarketingConsent() && !useNoCookiesBingMapsDomain) {
      setStatus('noConsentCookie');
      return;
    }

    if (firstLoad) {
      let oldScript: HTMLScriptElement | null = document.querySelector(
        `script[src="${src}"]`
      );
      if (oldScript) {
        oldScript.remove();
      }
      setFirstLoad(false);
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    let script: HTMLScriptElement | null = document.querySelector(
      `script[src="${src}"]`
    );

    if (!script) {
      // Create script
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.type = 'text/javascript';
      script.setAttribute('data-status', 'loading');

      // Add script to document body
      document.body.appendChild(script);

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: Event) => {
        (script as HTMLScriptElement).setAttribute('data-status', 'ready');
        setStatus('ready');
      };

      //@ts-ignore
      window[callbackName] = (e: Event) => {
        setAttributeFromEvent(e);
      };
    } else {
      // Grab existing script status from attribute and set to state.
      const newStatus = (script as HTMLScriptElement).getAttribute(
        'data-status'
      ) as statusValues;
      if (newStatus !== status) {
        setStatus(newStatus);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, src]);

  // Listen for changes in OptanonActiveGroups (cookie consent)
  useEffect(() => {
    window.Acr.helpers = {
      ...window.Acr.helpers,
      bingMapScriptConsent: () => {
        // Check if the component is still mounted before updating the state
        if (!hasMarketingConsent()) {
          setStatus('noConsentCookie');
        } else {
          // If consent is given, re-run the effect to load the script
          setStatus('loading');
        }
      },
    };

    const handleConsentChange = () => {
      window.Acr.helpers.bingMapScriptConsent();
    };

    // Check if OneTrust is available before attaching the listener
    if (
      typeof window.OneTrust !== 'undefined' &&
      typeof window.OneTrust.OnConsentChanged === 'function'
    ) {
      window.OneTrust.OnConsentChanged(handleConsentChange);
    } else {
      console.warn(
        'OneTrust is not available. The OnConsentChanged event will not be attached.'
      );
    }

    return () => {
      window.Acr.helpers.bingMapScriptConsent = () => {};
    };
  }, []);

  return status;
}
